// The KGOB Journal — Archive (year + month grouped)

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ARTICLES as ALL_ARTICLES, YEARS, ARTICLE_BODIES } from './data';
import { Masthead, Colophon } from './components';
import './blog.css';

// Only show essays that have a real long-form body in ARTICLE_BODIES.
const ARTICLES = (ALL_ARTICLES || []).filter((a) => a.bodyKey && ARTICLE_BODIES[a.bodyKey]);

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function JournalArchive() {
  const navigate = useNavigate();
  const openArticle = (a) => navigate(`/journal/${a.id}`);

  return (
    <div className="kgob-journal-root">
      <Helmet>
        <title>Archive · The KGOB Journal</title>
        <meta
          name="description"
          content="Complete archive of editorial essays from The KGOB Journal. Every piece since 2018, organized by year and month."
        />
        <link rel="canonical" href="https://kgob.com/journal/archive" />
        <meta property="og:title" content="Archive · The KGOB Journal" />
        <meta property="og:description" content="Complete archive of editorial essays from The KGOB Journal. Every piece since 2018, organized by year and month." />
        <meta property="og:url" content="https://kgob.com/journal/archive" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://kgob.com/share/og/v1/kgob-share-default.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Archive · The KGOB Journal" />
        <meta name="twitter:description" content="Complete archive of editorial essays from The KGOB Journal." />
        <meta name="twitter:image" content="https://kgob.com/share/og/v1/kgob-twitter-card.png" />
        <link
          href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300;0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,300;1,6..72,400;1,6..72,500&family=Geist:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Masthead />

      <main className="page" data-testid="journal-archive-page">
        <div className="archive-head">
          <h1>
            The <em>Archive</em>,<br />
            month by month.
          </h1>
          <p>
            Every essay published in The Journal since 2018, grouped by year, then by month. Effective-date stamps tell you which guidance still applies and which has been superseded.
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 28,
            padding: '14px 0',
            marginBottom: 36,
            borderTop: '1px solid var(--rule)',
            borderBottom: '1px solid var(--rule)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            color: 'var(--ink-soft)',
            letterSpacing: '0.04em',
            flexWrap: 'wrap',
          }}
        >
          <span><b style={{ color: 'var(--ink)' }}>{ARTICLES.length}</b> ESSAYS</span>
          <span><b style={{ color: 'var(--ink)' }}>{YEARS.length}</b> YEARS</span>
          <span><b style={{ color: 'var(--ink)' }}>{new Set((ARTICLES || []).map((a) => a.date.slice(0, 7))).size}</b> MONTHS</span>
          <span><b style={{ color: 'var(--ink)' }}>{new Set((ARTICLES || []).map((a) => a.author.name)).size}</b> AUTHORS</span>
          <span><b style={{ color: 'var(--ink)' }}>5</b> CATEGORIES</span>
          <span style={{ marginLeft: 'auto' }}>SORTED: NEWEST FIRST</span>
        </div>

        {(YEARS || []).map((year) => {
          const yearArticles = (ARTICLES || []).filter((a) => a.year === year).sort((a, b) => b.date.localeCompare(a.date));
          const cats = [...new Set((yearArticles || []).map((a) => a.category))];

          const monthsInYear = {};
          (yearArticles || []).forEach((a) => {
            const m = parseInt(a.date.slice(5, 7), 10) - 1;
            if (!monthsInYear[m]) monthsInYear[m] = [];
            monthsInYear[m].push(a);
          });
          const monthsList = Object.keys(monthsInYear).map(Number).sort((a, b) => b - a);

          return (
            <section className="year-block" key={year} data-testid={`journal-year-block-${year}`}>
              <div className="year-stamp">
                <div className="year-num">{year}</div>
                <div className="year-sub">
                  <span>
                    <b>{yearArticles.length}</b> essays · <b>{monthsList.length}</b> months
                  </span>
                  <span>{cats.length} categories</span>
                  <span
                    style={{
                      marginTop: 8,
                      fontFamily: 'var(--serif)',
                      fontStyle: 'italic',
                      fontSize: 13,
                      color: 'var(--ink-soft)',
                      letterSpacing: 'normal',
                    }}
                  >
                    {year === 2025 && 'The SECURE 2.0 year.'}
                    {year === 2024 && 'The transition year.'}
                    {year === 2023 && 'Energy credits & the IRA.'}
                    {year === 2022 && 'TCJA, five years on.'}
                  </span>
                </div>

                <div className="month-rail">
                  <div className="month-rail-label">Months</div>
                  {Array.from({ length: 12 }, (_, i) => 11 - i).map((m) => {
                    const has = !!monthsInYear[m];
                    return (
                      <a data-testid="journal-archive-a-1"
                        key={m}
                        href={has ? `#${year}-${MONTH_NAMES[m].toLowerCase()}` : undefined}
                        className={'month-tick ' + (has ? '' : 'month-tick--empty')}
                      >
                        <span className="month-tick-mon">{MONTH_NAMES[m].slice(0, 3)}</span>
                        <span className="month-tick-count">{has ? monthsInYear[m].length : ', '}</span>
                      </a>
                    );
                  })}
                </div>
              </div>

              <div className="year-list">
                {(monthsList || []).map((m) => (
                  <React.Fragment key={m}>
                    <div className="month-header" id={`${year}-${MONTH_NAMES[m].toLowerCase()}`}>
                      <span className="month-header-name">
                        {MONTH_NAMES[m]} {year}
                      </span>
                      <span className="month-header-count">
                        {monthsInYear[m].length} essay{monthsInYear[m].length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {(monthsInYear[m] || []).map((a) => {
                      const d = new Date(a.date);
                      const dateStr = d
                        .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        .toUpperCase();
                      return (
                        <div className="row" key={a.id} onClick={() => openArticle(a)} data-testid={`journal-archive-row-${a.id}`}>
                          <span className="row-date">{dateStr}</span>
                          <h3 className="row-title">
                            {a.title}
                            <span className="row-deck">{a.deck}</span>
                          </h3>
                          <span className="row-cat">{a.category}</span>
                          <span className="row-read">{a.readMin} min</span>
                          <span className="row-cite">¶ {a.citations} cit.</span>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </section>
          );
        })}
      </main>

      <Colophon />
    </div>
  );
}
